import React, { useEffect, useMemo, useState } from 'react';

import gridTokens from '@amzn/meridian-tokens/base/icon/grid';
import listTokens from '@amzn/meridian-tokens/base/icon/menu';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Text from '@amzn/meridian/text';

import { IVendorContact, mockContacts } from 'src/data/VendorContactsData';

import { ContactsCards } from './ContactCards';
import { ContactsTable } from './ContactTable';

export const VendorContacts: React.FC = () => {
    const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
    const [contactQuery, setContactQuery] = useState('');
    const [filteredContacts, setFilteredContacts] = useState<IVendorContact[]>(mockContacts);

    // Filter contacts whenever the search query changes
    useEffect(() => {
        if (!contactQuery.trim()) {
            // If search query is empty, show all contacts
            setFilteredContacts(mockContacts);
            return;
        }

        // Convert search query to lowercase for case-insensitive comparison
        const query = contactQuery.toLowerCase();

        // Filter contacts based on search query
        const filtered = mockContacts.filter((contact) => {
            // Check if any field contains the search query
            return (
                contact.name.toLowerCase().includes(query) ||
                contact.role.toLowerCase().includes(query) ||
                contact.location.toLowerCase().includes(query) ||
                contact.email.toLowerCase().includes(query) ||
                contact.phone.toLowerCase().includes(query) ||
                contact.function.toLowerCase().includes(query)
            );
        });

        setFilteredContacts(filtered);
    }, [contactQuery]);

    // Memoize the count of filtered contacts
    const filteredCount = useMemo(() => filteredContacts.length, [filteredContacts]);
    const totalCount = useMemo(() => mockContacts.length, []);

    return (
        <Column height="100%" spacing="300">
            <Row widths={['50%', '50%']} spacingInset="400">
                <SearchField
                    value={contactQuery}
                    onChange={setContactQuery}
                    placeholder="Search contacts..."
                    onSubmit={() => {}}
                    searchButton={true}
                    clearButton={true}
                />
                <Row alignmentHorizontal="end">
                    <Button
                        type={viewMode === 'table' ? 'primary' : 'secondary'}
                        onClick={() => setViewMode('table')}
                        size="small"
                    >
                        <Icon tokens={listTokens} />
                        Table
                    </Button>
                    <Button
                        type={viewMode === 'cards' ? 'primary' : 'secondary'}
                        onClick={() => setViewMode('cards')}
                        size="small"
                    >
                        <Icon tokens={gridTokens} />
                        Card
                    </Button>
                </Row>
            </Row>

            {/* Show filter results count when filtering */}
            {contactQuery.trim() && (
                <Row spacingInset="none 400 300 400">
                    <Text type="b200" color="secondary">
                        Showing {filteredCount} of {totalCount} contacts
                    </Text>
                </Row>
            )}

            {/* Show appropriate view with filtered contacts */}
            {filteredContacts.length > 0 ? (
                viewMode === 'cards' ? (
                    <ContactsCards contacts={filteredContacts} />
                ) : (
                    <ContactsTable contacts={filteredContacts} />
                )
            ) : (
                <Column alignmentHorizontal="center" spacingInset="600">
                    <Text type="h200">No contacts match your search</Text>
                    <Text type="b200" color="secondary">
                        Try adjusting your search terms
                    </Text>
                </Column>
            )}
        </Column>
    );
};
